import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Tooltip } from '@fluentui/react-components';
import {
    ArrowDownload16Regular,
    CheckboxChecked16Regular,
    Copy16Regular,
    DocumentAdd16Regular,
    Eye16Regular,
    Info16Regular,
    Notebook16Regular,
    ShieldError16Regular,
    Sparkle16Filled,
} from '@fluentui/react-icons';
import { ThinkingOrb } from 'thinking-orbs';
import AssistantResponseContent from './structured/AssistantResponseContent';
import InsufficientEvidenceBanner from '../Dataroom2.0/intelligence/InsufficientEvidenceBanner';
import AskAiComposer from './AskAiComposer';
import AskAiEvidenceSidebar from './AskAiEvidenceSidebar';
import AskAiQuickCreateSidebar from './AskAiQuickCreateSidebar';
import ResponseActionsFooter from './ResponseActionsFooter';
import EvidenceSummary from './EvidenceSummary';
import StreamingAssistantMessage from './streaming/StreamingAssistantMessage';
import AskLensOriginIndicator from './AskLensOriginIndicator';
import {
    formatInsightsOriginIndicator,
    isInsightsModuleAskOrigin,
    insightsModuleAskTooltip,
} from '../../utils/intelligenceInsightsAskLensOrigin';
import { useStreamingScroll } from './streaming/useStreamingScroll';
import { ObservationFormSidebar } from '../Observations';
import { PostInformationRequestSidebar } from '../IRL';
import TaskFormSidebar from '../TaskManagement/TaskFormSidebar';
import { evidenceToRagChunks } from '../../redux/intelligenceSlice';
import {
    ACCURACY_INACTIVE_REASON_MASTER_FLAG,
    resolveAccuracyModeStatus,
    resolveIntelligenceAbstention,
} from '../../utils/accuracyModeMeta';
import {
    buildAccuracyEvidencePanelModel,
    isAccuracyEvidencePanelAvailable,
} from '../../utils/accuracyEvidenceMeta';
import {
    assistantMessageToAskData,
    contextModeLabel,
    stripInlineCitationMarkers,
} from '../../utils/askAiChatMeta';
import {
    resolveResponseActions,
    shouldUseContextualFooter,
} from '../../utils/responseActions';
import {
    ASK_ACTION_ACTION_LOG,
    ASK_ACTION_OBSERVATION,
    ASK_ACTION_RAID,
    ASK_ACTION_TASK,
    resolveAskSuggestedActions,
} from '../../utils/askAiSuggestedActions';
import {
    isDbToolResponse,
    isVdrCatalogResponse,
    resolveTrustStripEvidenceBand,
    resolveTrustStripRoutingBadges,
} from '../../utils/trustStripMeta';
import {
    evidenceCoverageBandFromPercent,
    extractLensIntelligenceBadges,
} from '../../utils/lensIntelligenceResponseMeta';
import { formatMessageTime } from '../Journal/Analysis/analysisUtils';
import { getInitials, htmlToPlainText } from '../../utils/commonUtils';
import { getShortDisplayName } from '../Journal/journalUtils';
import { saveAskLensOutputToJournal } from '../../utils/askLensJournal';
import { markdownToJournalHtml } from '../../utils/markdownToJournalHtml';
import { markdownToPdfHtml } from '../../utils/markdownToPdfHtml';
import { exportNoteAsPdf } from '../Journal/exportNote';
import { buildExportPdfFilename } from '../../utils/exportPdfFilename';
import streamingStyles from './streaming/streaming.module.css';
import styles from './askAi.module.css';

const TRUST_DOT_CLASS = {
    success: styles.trustDotSuccess,
    warning: styles.trustDotWarning,
    danger: styles.trustDotDanger,
    neutral: styles.trustDotNeutral,
};

function resolveEvidenceTotals(retrievalDebug, usedCount) {
    const debug =
        retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const coverage =
        debug.coverage_report && typeof debug.coverage_report === 'object'
            ? debug.coverage_report
            : {};
    const filter =
        (debug.evidence_filter && typeof debug.evidence_filter === 'object'
            ? debug.evidence_filter
            : null) ||
        (coverage.evidence_filter && typeof coverage.evidence_filter === 'object'
            ? coverage.evidence_filter
            : {});
    const included = Number(filter.included_count);
    const excluded = Number(filter.excluded_count);
    const contextOnly = Number(filter.context_only_count);
    const contradiction = Number(filter.contradiction_candidate_count);
    const parts = [included, excluded, contextOnly, contradiction].filter(n =>
        Number.isFinite(n),
    );
    const totalFromFilter = parts.length ? parts.reduce((a, b) => a + b, 0) : null;
    const used = Number.isFinite(included) && included > 0 ? included : usedCount;
    const total =
        totalFromFilter != null && totalFromFilter > 0 ? totalFromFilter : used;
    return { used, total };
}

function resolveCoveragePct(retrievalDebug) {
    const rd = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const cr = rd.coverage_report && typeof rd.coverage_report === 'object' ? rd.coverage_report : {};
    const raw =
        cr.coverage_pct ??
        cr.citation_coverage ??
        rd.citation_coverage ??
        null;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return n <= 1 ? n * 100 : n;
}

function resolveCatalogFileCount(retrievalDebug) {
    const rd = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const catalog = rd.vdr_catalog && typeof rd.vdr_catalog === 'object' ? rd.vdr_catalog : {};
    const total = Number(
        catalog.total ??
            catalog.total_files ??
            catalog.listed ??
            ((Number(catalog.vdr_files) || 0) +
                (Number(catalog.irls) || 0) +
                (Number(catalog.observations) || 0) ||
                null) ??
            rd.permitted_vdr_file_count ??
            0,
    );
    return Number.isFinite(total) && total > 0 ? total : 0;
}

function TrustStripItem({ label, tone = 'neutral' }) {
    return (
        <span className={styles.trustItem}>
            <span
                className={`${styles.trustDot} ${TRUST_DOT_CLASS[tone] || TRUST_DOT_CLASS.neutral}`}
                aria-hidden
            />
            {label}
        </span>
    );
}

function AssistantMessage({
    message,
    contextMode,
    insightsOrigin = null,
    citationDisplayCtx,
    onViewEvidence,
    onCopy,
    onSaveJournal,
    onCreateObservation,
    onCreateTask,
    onCreateIrl,
    onCreateRaid,
    onCreateActionLog,
    onExportPdf,
    onFollowUpQuestion,
    navigate,
    projectId,
}) {
    const data = useMemo(() => assistantMessageToAskData(message), [message]);
    const citations = data?.citations || [];
    const evidence = data?.evidence || [];
    const chunks = useMemo(() => evidenceToRagChunks(evidence, citations), [evidence, citations]);
    const abstention = resolveIntelligenceAbstention({
        answer: data?.answer,
        retrievalDebug: data?.retrieval_debug,
        evidenceGovernance: data?.evidence_governance,
    });
    const rawAnswer = String(data?.answer || '').trim();
    const answer = stripInlineCitationMarkers(rawAnswer);
    const confidence = data?.confidence;
    const coveragePct = resolveCoveragePct(data?.retrieval_debug);
    const isCatalog = isVdrCatalogResponse(data?.retrieval_debug);
    const isDbTools = isDbToolResponse(data?.retrieval_debug);
    const isStructuredAnswer = isCatalog || isDbTools;
    const catalogFileCount = isCatalog ? resolveCatalogFileCount(data?.retrieval_debug) : 0;
    const sourceCount = isCatalog ? catalogFileCount : citations.length;

    const responseActions = useMemo(
        () =>
            resolveResponseActions({
                responseActions: data?.response_actions,
                meta: message?.meta,
            }),
        [data?.response_actions, message?.meta],
    );

    const hasResponseActionsField =
        data?.response_actions != null ||
        (message?.meta && message.meta.response_actions != null);

    const useContextualFooter = shouldUseContextualFooter(responseActions, {
        askData: data,
        hasResponseActionsField,
    });

    const suggestedActions = useMemo(
        () =>
            resolveAskSuggestedActions({
                suggestedActions: data?.suggested_actions,
                responseActions,
                hasResponseActionsField,
                question: data?.question || '',
                answer,
                retrievalDebug: data?.retrieval_debug,
                isAbstention: Boolean(abstention?.isAbstention) || isStructuredAnswer,
            }),
        [
            data,
            answer,
            abstention?.isAbstention,
            isStructuredAnswer,
            responseActions,
            hasResponseActionsField,
        ],
    );

    const evidenceModel = useMemo(() => {
        if (isStructuredAnswer) return null;
        const canBuild =
            citations.length > 0 ||
            isAccuracyEvidencePanelAvailable(true, data?.retrieval_debug, citations, evidence);
        if (!canBuild) return null;
        return buildAccuracyEvidencePanelModel({
            citations,
            evidence,
            retrievalDebug: data?.retrieval_debug,
            chunks,
            ctx: citationDisplayCtx,
        });
    }, [data, citations, evidence, chunks, citationDisplayCtx, isStructuredAnswer]);

    const badges = useMemo(
        () =>
            extractLensIntelligenceBadges({
                retrievalDebug: data?.retrieval_debug,
                topLevelConfidence: confidence,
                isVerified: true,
            }),
        [data?.retrieval_debug, confidence],
    );

    const accuracyModeStatus = useMemo(
        () => resolveAccuracyModeStatus(data?.retrieval_debug),
        [data?.retrieval_debug],
    );
    const showAccuracyInactiveBanner =
        accuracyModeStatus.requested &&
        !accuracyModeStatus.active &&
        !isStructuredAnswer;

    const routingBadges = useMemo(
        () => resolveTrustStripRoutingBadges(data?.retrieval_debug),
        [data?.retrieval_debug],
    );

    const confidenceLabel = badges.confidence
        ? `${badges.confidence} confidence`
        : confidence
          ? `${String(confidence).charAt(0).toUpperCase()}${String(confidence).slice(1)} confidence`
          : null;
    const confidenceTone = isStructuredAnswer
        ? 'success'
        : badges.confidenceTone || 'neutral';

    const supplementalEvidenceBand = useMemo(() => {
        if (isStructuredAnswer) return null;
        if (badges.evidenceCoverage) {
            return {
                label: badges.evidenceCoverage,
                tone: badges.evidenceCoverageTone || 'neutral',
            };
        }
        const pct = resolveCoveragePct(data?.retrieval_debug);
        if (pct != null) {
            const band = evidenceCoverageBandFromPercent(pct);
            if (band) return band;
        }
        return resolveTrustStripEvidenceBand(data?.retrieval_debug, sourceCount);
    }, [badges, data?.retrieval_debug, isStructuredAnswer, sourceCount]);

    const routingLabels = useMemo(
        () => new Set(routingBadges.map(badge => String(badge.label).toLowerCase())),
        [routingBadges],
    );

    const evidenceBand =
        supplementalEvidenceBand &&
        !routingLabels.has(String(supplementalEvidenceBand.label).toLowerCase())
            ? supplementalEvidenceBand
            : null;

    const openEvidence = () => {
        if (!evidenceModel) return;
        onViewEvidence?.({
            model: evidenceModel,
            citations,
            chunks,
            confidence,
            coveragePct,
            retrievalDebug: data?.retrieval_debug,
        });
    };

    const showTrust =
        confidenceLabel ||
        routingBadges.length > 0 ||
        evidenceBand?.label ||
        sourceCount > 0;

    const trustStripLabels = useMemo(() => {
        const labels = [];
        if (confidenceLabel) labels.push(confidenceLabel);
        routingBadges.forEach(badge => labels.push(badge.label));
        if (evidenceBand?.label) labels.push(evidenceBand.label);
        return labels;
    }, [confidenceLabel, routingBadges, evidenceBand?.label]);

    const showEvidenceSummary =
        !abstention.isAbstention &&
        !isStructuredAnswer &&
        (citations.length > 0 || evidenceModel || sourceCount > 0);

    const coverageLabel =
        coveragePct != null && Number.isFinite(Number(coveragePct))
            ? `${Math.round(Number(coveragePct))}% coverage`
            : null;

    // Answer-first: render markdown as returned (tables/mermaid/charts).
    // Do not reorder into Executive Summary / Key Findings consultant sections.

    const moduleAsk = isInsightsModuleAskOrigin(insightsOrigin);
    const contextLabel = moduleAsk
        ? formatInsightsOriginIndicator(insightsOrigin)
        : contextModeLabel(contextMode);

    return (
        <>
            <div className={styles.messageMeta}>
                <span className={styles.messageName}>Lens</span>
                <span className={styles.messageTime}>{formatMessageTime(message.created_at)}</span>
                <span
                    className={
                        moduleAsk ? `${styles.contextPill} ${styles.contextPillInsights}` : styles.contextPill
                    }
                    title={moduleAsk ? insightsModuleAskTooltip(insightsOrigin) : undefined}>
                    {contextLabel}
                </span>
            </div>
            {showAccuracyInactiveBanner ? (
                <div
                    className={styles.accuracyModeInactiveBanner}
                    role="status"
                    aria-label="Verified accuracy mode inactive on server">
                    <strong>Verified mode inactive on server</strong>
                    <span>
                        {accuracyModeStatus.inactiveReason ||
                            badges.inactiveReason ||
                            ACCURACY_INACTIVE_REASON_MASTER_FLAG}
                    </span>
                </div>
            ) : null}
            {abstention.isAbstention ? (
                <InsufficientEvidenceBanner title={abstention.title} body={abstention.body} />
            ) : (
                <AssistantResponseContent
                    answer={answer}
                    responseBlocks={[]}
                    citations={citations}
                    chunks={chunks}
                    citationScope=""
                    onViewEvidence={evidenceModel ? openEvidence : undefined}
                    consultantLayout={false}
                    className={styles.assistantMarkdown}
                />
            )}
            {showTrust ? (
                <div className={styles.trustStrip}>
                    <div className={styles.trustStripMain}>
                        {confidenceLabel ? (
                            <TrustStripItem label={confidenceLabel} tone={confidenceTone} />
                        ) : null}
                        {coverageLabel ? (
                            <>
                                {confidenceLabel ? (
                                    <span className={styles.trustSep} aria-hidden>
                                        ·
                                    </span>
                                ) : null}
                                <span className={styles.trustSourcesCount}>{coverageLabel}</span>
                            </>
                        ) : null}
                        {routingBadges.map((badge, index) => (
                            <React.Fragment key={badge.key}>
                                {confidenceLabel || coverageLabel || index > 0 ? (
                                    <span className={styles.trustSep} aria-hidden>
                                        ·
                                    </span>
                                ) : null}
                                <TrustStripItem label={badge.label} tone={badge.tone} />
                            </React.Fragment>
                        ))}
                        {evidenceBand?.label ? (
                            <>
                                {confidenceLabel || coverageLabel || routingBadges.length > 0 ? (
                                    <span className={styles.trustSep} aria-hidden>
                                        ·
                                    </span>
                                ) : null}
                                <TrustStripItem
                                    label={evidenceBand.label}
                                    tone={evidenceBand.tone}
                                />
                            </>
                        ) : null}
                        {sourceCount > 0 ? (
                            <>
                                <span className={styles.trustSep} aria-hidden>
                                    ·
                                </span>
                                <span className={styles.trustSourcesCount}>
                                    {isCatalog
                                        ? `${sourceCount} file${sourceCount === 1 ? '' : 's'}`
                                        : `${sourceCount} source${sourceCount === 1 ? '' : 's'}`}
                                </span>
                                {!isStructuredAnswer ? (
                                    <>
                                        <span className={styles.trustSep} aria-hidden>
                                            ·
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.viewEvidenceLink}
                                            onClick={openEvidence}>
                                            View evidence
                                        </button>
                                    </>
                                ) : null}
                            </>
                        ) : evidenceModel ? (
                            <>
                                {(confidenceLabel || coverageLabel || evidenceBand?.label) ? (
                                    <span className={styles.trustSep} aria-hidden>
                                        ·
                                    </span>
                                ) : null}
                                <button
                                    type="button"
                                    className={styles.viewEvidenceLink}
                                    onClick={openEvidence}>
                                    View evidence
                                </button>
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}
            {showEvidenceSummary ? (
                <EvidenceSummary
                    citations={citations}
                    chunks={chunks}
                    retrievalDebug={data?.retrieval_debug}
                    confidence={confidence}
                    evidenceGovernance={data?.evidence_governance}
                    trustStripLabels={trustStripLabels}
                    onViewEvidence={evidenceModel ? openEvidence : undefined}
                    collapsible
                    defaultExpanded={false}
                />
            ) : null}
            {useContextualFooter ? (
                <ResponseActionsFooter
                    responseActions={responseActions}
                    askData={data}
                    answerText={answer}
                    traceId={data?.trace_id}
                    projectId={projectId}
                    navigate={navigate}
                    onCopy={onCopy}
                    onSaveJournal={content => onSaveJournal(content, message)}
                    onExportPdf={onExportPdf}
                    onViewEvidence={openEvidence}
                    onOpenSource={() => openEvidence()}
                    onCreateObservation={onCreateObservation}
                    onCreateTask={onCreateTask}
                    onCreateIrl={onCreateIrl}
                    onCreateRaid={onCreateRaid}
                    onCreateActionLog={onCreateActionLog}
                    onFollowUpQuestion={onFollowUpQuestion}
                />
            ) : (
            <div className={styles.messageActions}>
                <div className={styles.messageActionChips}>
                    <button type="button" className={styles.actionChip} onClick={() => onCopy(answer)}>
                        <Copy16Regular /> Copy
                    </button>
                    <button
                        type="button"
                        className={styles.actionChip}
                        onClick={() => onSaveJournal(answer, message)}>
                        <DocumentAdd16Regular /> Save to Journal
                    </button>
                    {suggestedActions.includes(ASK_ACTION_OBSERVATION) ? (
                        <button
                            type="button"
                            className={styles.actionChip}
                            onClick={() => onCreateObservation(answer)}>
                            <Eye16Regular /> Create Observation
                        </button>
                    ) : null}
                    {suggestedActions.includes(ASK_ACTION_TASK) ? (
                        <button
                            type="button"
                            className={styles.actionChip}
                            onClick={() => onCreateTask(answer)}>
                            <CheckboxChecked16Regular /> Create Task
                        </button>
                    ) : null}
                    {suggestedActions.includes(ASK_ACTION_RAID) ? (
                        <button
                            type="button"
                            className={styles.actionChip}
                            onClick={() => onCreateRaid(answer)}>
                            <ShieldError16Regular /> Create RAID
                        </button>
                    ) : null}
                    {suggestedActions.includes(ASK_ACTION_ACTION_LOG) ? (
                        <button
                            type="button"
                            className={styles.actionChip}
                            onClick={() => onCreateActionLog(answer)}>
                            <Notebook16Regular /> Create Action Log
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className={styles.actionChip}
                        onClick={() => onExportPdf(answer)}>
                        <ArrowDownload16Regular /> Export PDF
                    </button>
                </div>
            </div>
            )}
        </>
    );
}

function ContextChangeDivider({ message }) {
    return (
        <div className={styles.contextChangeDivider} role="status">
            <div className={styles.contextChangeLine} aria-hidden />
            <div className={styles.contextChangeBody}>
                <Info16Regular className={styles.contextChangeIcon} />
                <span>{message.content}</span>
            </div>
            <div className={styles.contextChangeLine} aria-hidden />
        </div>
    );
}

const AskAiChatThread = ({
    messages = [],
    messagesLoading = false,
    submitting = false,
    streamingState = null,
    question,
    onQuestionChange,
    contextMode,
    onContextModeChange,
    onCustomSelect,
    onOpenChangeContext,
    onOpenSavedPrompts,
    onSubmit,
    citationDisplayCtx,
    onInlineCitationClick,
    onEvidenceItemClick,
    sourcesRailOpen = false,
    sourcesRail = null,
    sourcesReady = null,
    sourcesProcessing = null,
    insightsOrigin = null,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectedProject = useSelector(state => state.projectData?.selectedProject);
    const projectId =
        typeof localStorage !== 'undefined' ? localStorage.getItem('project_id') : null;
    const notesState = useSelector(state => state.dashboard?.notes || []);
    const user = useSelector(state => state.user?.user);
    const bottomRef = useRef(null);
    const threadScrollRef = useRef(null);
    const [showObsSidebar, setShowObsSidebar] = useState(false);
    const [showTaskSidebar, setShowTaskSidebar] = useState(false);
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showActionLogSidebar, setShowActionLogSidebar] = useState(false);
    const [sidebarContent, setSidebarContent] = useState('');
    const [evidenceSidebar, setEvidenceSidebar] = useState(null);

    const userFullName = user?.full_name || '';
    const userDisplayName = getShortDisplayName(userFullName) || userFullName || 'You';
    const userInitials = getInitials(userFullName) || 'Y';
    const moduleAsk = isInsightsModuleAskOrigin(insightsOrigin);

    const streamingActive = Boolean(streamingState?.active);
    const scrollWatchDeps = useMemo(
        () => [
            messages,
            submitting,
            streamingState?.displayContent,
            streamingState?.completedBlocks?.length,
            streamingState?.phase,
        ],
        [messages, submitting, streamingState],
    );
    const { showJumpToLatest, jumpToLatest } = useStreamingScroll({
        containerRef: threadScrollRef,
        bottomRef,
        watchDeps: scrollWatchDeps,
    });

    const handleCopy = async content => {
        try {
            await navigator.clipboard.writeText(content || '');
            toast.success('Copied');
        } catch {
            toast.error('Could not copy');
        }
    };

    const handleSaveJournal = async (content, message) => {
        const query =
            [...messages].reverse().find(m => m.role === 'user')?.content || 'Ask Lens output';
        try {
            await saveAskLensOutputToJournal({
                dispatch,
                notesState,
                query,
                outputText: content,
            });
            toast.success('Saved to Journal');
        } catch {
            toast.error('Could not save to Journal');
        }
    };

    const handleExportPdf = async content => {
        try {
            const bodyHtml = await markdownToPdfHtml(content || '');
            await exportNoteAsPdf({
                bodyHtml: bodyHtml || '<p></p>',
                includeTitle: false,
                filename: buildExportPdfFilename({
                    project: selectedProject,
                    label: 'Lens Intelligence',
                }),
            });
        } catch {
            toast.error('Could not export PDF');
        }
    };

    const evidenceTotals = evidenceSidebar
        ? resolveEvidenceTotals(
              evidenceSidebar.retrievalDebug,
              (evidenceSidebar.citations || []).length,
          )
        : null;

    return (
        <div className={`${styles.threadLayout} ${sourcesRailOpen ? styles.threadWithRail : ''}`}>
            <div className={styles.threadMain}>
                <div className={styles.threadScroll} ref={threadScrollRef}>
                    <div className={styles.threadColumn}>
                        <div className={styles.threadContextBar}>
                            {moduleAsk ? (
                                <AskLensOriginIndicator
                                    origin={insightsOrigin}
                                    className={styles.originIndicatorThreadBar}
                                />
                            ) : (
                                <>
                                    <div className={styles.threadContextLeft}>
                                        <Tooltip
                                            content="Context controls which project information Lens can use when answering this chat."
                                            relationship="description"
                                            positioning="below"
                                            withArrow>
                                            <button
                                                type="button"
                                                className={styles.threadContextInfoBtn}
                                                aria-label="About context">
                                                <Info16Regular className={styles.threadContextIcon} />
                                            </button>
                                        </Tooltip>
                                        <span className={styles.threadContextLabel}>Context:</span>
                                        <span className={styles.threadContextValue}>
                                            {contextModeLabel(contextMode)}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.changeContextBtn}
                                        onClick={() => onOpenChangeContext?.()}>
                                        Change context
                                    </button>
                                </>
                            )}
                        </div>

                        {messagesLoading && !messages.length ? (
                            <p className={styles.historyEmpty}>Loading conversation…</p>
                        ) : null}
                        {messages.map(msg => {
                            if (msg.role === 'system') {
                                return (
                                    <div key={msg.id} className={styles.systemMessageBlock}>
                                        <ContextChangeDivider message={msg} />
                                    </div>
                                );
                            }
                            const isUser = msg.role === 'user';
                            return (
                                <div key={msg.id} className={styles.messageBlock}>
                                    <div className={isUser ? styles.avatarUser : styles.avatarAi}>
                                        {isUser ? (
                                            userInitials
                                        ) : (
                                            <Sparkle16Filled color="#ffffff" />
                                        )}
                                    </div>
                                    <div className={styles.messageContent}>
                                        {isUser ? (
                                            <>
                                                <div className={styles.messageMeta}>
                                                    <span className={styles.messageName}>
                                                        {userDisplayName}
                                                    </span>
                                                    <span className={styles.messageTime}>
                                                        {formatMessageTime(msg.created_at)}
                                                    </span>
                                                </div>
                                                <div className={styles.userBubble}>{msg.content}</div>
                                            </>
                                        ) : (
                                            <AssistantMessage
                                                message={msg}
                                                contextMode={contextMode}
                                                insightsOrigin={insightsOrigin}
                                                citationDisplayCtx={citationDisplayCtx}
                                                onViewEvidence={payload =>
                                                    setEvidenceSidebar(payload)
                                                }
                                                onCopy={handleCopy}
                                                onSaveJournal={handleSaveJournal}
                                                onCreateObservation={text => {
                                                    setSidebarContent(
                                                        markdownToJournalHtml(text || '') || text,
                                                    );
                                                    setShowObsSidebar(true);
                                                }}
                                                onCreateTask={text => {
                                                    setSidebarContent(text || '');
                                                    setShowTaskSidebar(true);
                                                }}
                                                onCreateIrl={text => {
                                                    setSidebarContent(text || '');
                                                    setShowIrlSidebar(true);
                                                }}
                                                onCreateRaid={text => {
                                                    setSidebarContent(htmlToPlainText(text || ''));
                                                    setShowRaidSidebar(true);
                                                }}
                                                onCreateActionLog={text => {
                                                    setSidebarContent(htmlToPlainText(text || ''));
                                                    setShowActionLogSidebar(true);
                                                }}
                                                onExportPdf={handleExportPdf}
                                                onFollowUpQuestion={onQuestionChange}
                                                navigate={navigate}
                                                projectId={projectId}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {streamingActive ? (
                            <StreamingAssistantMessage
                                streamState={streamingState}
                                contextMode={contextMode}
                            />
                        ) : submitting ? (
                            <div className={styles.thinkingLoaderWrap} role="status" aria-live="polite">
                                <div className={styles.thinkingLoader}>
                                    <div className={styles.thinkingLoaderOrb}>
                                        <ThinkingOrb
                                            state="composing"
                                            size={64}
                                            speed={2}
                                            theme="light"
                                            aria-label="Thinking"
                                        />
                                    </div>
                                    <span className={styles.thinkingLoaderLabel}>Thinking…</span>
                                </div>
                            </div>
                        ) : null}
                        {showJumpToLatest ? (
                            <div className={streamingStyles.jumpToLatestWrap}>
                                <button
                                    type="button"
                                    className={streamingStyles.jumpToLatestBtn}
                                    onClick={jumpToLatest}>
                                    Jump to latest
                                </button>
                            </div>
                        ) : null}
                        <div ref={bottomRef} />
                    </div>
                </div>

                <div className={styles.threadComposer}>
                    <div className={styles.threadColumn}>
                        <AskAiComposer
                            variant="thread"
                            question={question}
                            onQuestionChange={onQuestionChange}
                            contextMode={contextMode}
                            onContextModeChange={onContextModeChange}
                            onCustomSelect={onCustomSelect}
                            onOpenChangeContext={onOpenChangeContext}
                            onOpenSavedPrompts={onOpenSavedPrompts}
                            onSubmit={onSubmit}
                            submitting={submitting}
                            showHint
                            sourcesReady={sourcesReady}
                            sourcesProcessing={sourcesProcessing}
                            insightsOrigin={insightsOrigin}
                        />
                    </div>
                </div>
            </div>

            {sourcesRailOpen && sourcesRail ? (
                <aside className={styles.sourcesRail}>{sourcesRail}</aside>
            ) : null}

            {evidenceSidebar ? (
                <AskAiEvidenceSidebar
                    open
                    onClose={() => setEvidenceSidebar(null)}
                    model={evidenceSidebar.model}
                    citations={evidenceSidebar.citations}
                    chunks={evidenceSidebar.chunks}
                    confidence={evidenceSidebar.confidence}
                    coveragePct={evidenceSidebar.coveragePct}
                    sourcesUsedCount={evidenceTotals?.used}
                    sourcesTotalCount={evidenceTotals?.total}
                    processingCount={sourcesProcessing}
                    onItemClick={onEvidenceItemClick}
                />
            ) : null}

            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={() => sidebarContent || ''}
            />
            <TaskFormSidebar
                show={showTaskSidebar}
                setShow={show => {
                    setShowTaskSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultTask={() => htmlToPlainText(sidebarContent || '')}
            />
            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showActionLogSidebar}
                setShow={show => {
                    setShowActionLogSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="action_log"
                defaultText={() => sidebarContent || ''}
            />
        </div>
    );
};

export default AskAiChatThread;
